import { optionalPositiveInteger, optionalTrimmedString } from '../../../lib/routeSearch';
import {
  defaultPlacementsFilter,
  isPlacementSortProperty,
  PlacementSortDirection,
  PlacementStatus,
  PlacementType,
  type PlacementsFilter,
} from './model';

export interface PlacementsRouteSearch {
  search?: string;
  statuses?: string;
  types?: string;
  page?: number;
  size?: number;
  sortProperty?: string | null;
  sortDirection?: PlacementSortDirection;
}

export function placementsRouteSearchSchema(
  search: Record<string, unknown>,
): PlacementsRouteSearch {
  const searchTerm = optionalTrimmedString(search['search']);
  const statuses = typeof search['statuses'] === 'string' ? search['statuses'] : undefined;
  const types = typeof search['types'] === 'string' ? search['types'] : undefined;
  const page = optionalPositiveInteger(search['page']);
  const size = optionalPositiveInteger(search['size']);
  const sortProperty = optionalTrimmedString(search['sortProperty']);
  const sortDirection = Object.values(PlacementSortDirection).includes(
    search['sortDirection'] as PlacementSortDirection,
  )
    ? (search['sortDirection'] as PlacementSortDirection)
    : undefined;
  return {
    ...(searchTerm ? { search: searchTerm } : {}),
    ...(statuses !== undefined ? { statuses } : {}),
    ...(types !== undefined ? { types } : {}),
    ...(page ? { page } : {}),
    ...(size ? { size } : {}),
    ...(sortProperty && isPlacementSortProperty(sortProperty) ? { sortProperty } : {}),
    ...(search['sortProperty'] === null ? { sortProperty: null } : {}),
    ...(sortDirection ? { sortDirection } : {}),
  };
}

export function parsePlacementsRouteSearch(search: PlacementsRouteSearch): PlacementsFilter {
  return {
    searchTerm: search.search ?? defaultPlacementsFilter.searchTerm,
    statuses: parseEnumList(search.statuses, PlacementStatus, defaultPlacementsFilter.statuses),
    types: parseEnumList(search.types, PlacementType, defaultPlacementsFilter.types),
    page: search.page ? search.page - 1 : defaultPlacementsFilter.page,
    size: search.size ?? defaultPlacementsFilter.size,
    sortProperty:
      search.sortProperty === null ||
      (search.sortProperty && isPlacementSortProperty(search.sortProperty))
        ? search.sortProperty
        : defaultPlacementsFilter.sortProperty,
    sortDirection: search.sortDirection ?? defaultPlacementsFilter.sortDirection,
  };
}

export function serializePlacementsRouteSearch(filter: PlacementsFilter): PlacementsRouteSearch {
  return {
    ...(filter.searchTerm.trim() ? { search: filter.searchTerm.trim() } : {}),
    ...(!sameSet(filter.statuses, defaultPlacementsFilter.statuses)
      ? { statuses: filter.statuses.join(',') }
      : {}),
    ...(filter.types.length ? { types: filter.types.join(',') } : {}),
    ...(filter.page ? { page: filter.page + 1 } : {}),
    ...(filter.size !== defaultPlacementsFilter.size ? { size: filter.size } : {}),
    ...(filter.sortProperty !== defaultPlacementsFilter.sortProperty
      ? { sortProperty: filter.sortProperty }
      : {}),
    ...(filter.sortDirection !== defaultPlacementsFilter.sortDirection
      ? { sortDirection: filter.sortDirection }
      : {}),
  };
}

function parseEnumList<T extends string>(
  value: string | undefined,
  enumObject: Record<string, T>,
  fallback: T[],
) {
  if (value === undefined) return fallback;
  const allowed = new Set(Object.values(enumObject));
  return value.split(',').filter((item): item is T => allowed.has(item as T));
}

function sameSet<T>(left: T[], right: T[]) {
  const rightSet = new Set(right);
  return left.length === right.length && left.every((value) => rightSet.has(value));
}
